"use client";

import { useState } from "react";
import { Flag, Check, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { addReport } from "@/lib/reports";

interface ReportIssueProps {
  courseSlug: string;
  courseTitle: string;
  /** "Course overview", or the lesson title. */
  pageLabel: string;
  lessonTitle?: string;
  className?: string;
}

/**
 * "Report an issue" link that opens an inline form on any course page. The
 * report saves to the demo store (lib/reports); ticking the 1-to-1 instructor
 * box surfaces it in the course workspace for the learner and their instructor
 * to go through together.
 */
export function ReportIssue({
  courseSlug,
  courseTitle,
  pageLabel,
  lessonTitle,
  className,
}: ReportIssueProps) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [flag, setFlag] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  function reset() {
    setMessage("");
    setFlag(false);
    setError("");
    setDone(false);
  }

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const text = message.trim();
    if (text.length < 5) {
      setError("Please add a little more detail (at least a few words).");
      return;
    }
    addReport({
      courseSlug,
      courseTitle,
      pageLabel,
      lessonTitle,
      message: text,
      flagForInstructor: flag,
    });
    setDone(true);
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          "inline-flex items-center gap-1.5 text-sm font-medium text-mid underline-offset-2 transition-colors hover:text-primary hover:underline",
          className,
        )}
      >
        <Flag className="size-3.5" aria-hidden="true" />
        Report an issue with this page
      </button>
    );
  }

  return (
    <div
      className={cn(
        "rounded-md border border-line bg-paper p-5 shadow-sm",
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-heading text-base font-semibold text-dark">
            Report an issue
          </h3>
          <p className="mt-0.5 text-xs text-mid">
            About: <span className="font-medium text-dark">{pageLabel}</span>
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            reset();
          }}
          aria-label="Close"
          className="-m-2 inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-mid transition-colors hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary"
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      </div>

      {done ? (
        <div className="mt-4">
          <p className="flex items-start gap-2 text-sm text-dark">
            <Check className="mt-0.5 size-4 shrink-0 text-success" aria-hidden="true" />
            <span>
              Thanks, this has been reported.
              {flag
                ? " Your 1-to-1 instructor will see it in your course workspace to go through with you."
                : ""}
            </span>
          </p>
          <div className="mt-4 flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={reset}
            >
              Report another
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setOpen(false);
                reset();
              }}
            >
              Done
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor={`report-${courseSlug}-${pageLabel}`}
              className="text-sm font-semibold text-dark"
            >
              What is the issue, or what needs clarifying?
            </label>
            <textarea
              id={`report-${courseSlug}-${pageLabel}`}
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Describe the problem, a mistake, or where you'd like more clarity…"
              className={cn(
                "min-h-[96px] w-full rounded-md border bg-paper px-4 py-2.5 text-base text-dark placeholder:text-mid/70",
                "transition-colors duration-150 hover:border-mid/50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary",
                error ? "border-error" : "border-line",
              )}
            />
            {error && <p className="text-sm font-medium text-error">{error}</p>}
          </div>

          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={flag}
              onChange={(e) => setFlag(e.target.checked)}
              className="mt-0.5 size-5 shrink-0 cursor-pointer rounded border-line accent-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary"
            />
            <span className="text-sm leading-snug text-mid">
              Flag this for my{" "}
              <span className="font-semibold text-dark">1-to-1 instructor</span>{" "}
              to go through with me. It will appear in my course workspace and in
              my manager&rsquo;s overview.
            </span>
          </label>

          <div className="flex gap-2">
            <Button type="submit" size="sm">
              Send report
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setOpen(false);
                reset();
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
